import React, { forwardRef } from 'react'
import type {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import {
  KibChevronDownIcon,
  KibChevronUpIcon,
  KibChewyIcon,
} from '@chewy/kib-icons-react'
import classNames from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import {
  Defaults,
  LanguageUtils,
  type Nil,
  NumberUtils,
  Utils,
} from '@pbt/pbt-ui-components'

import type {
  ModifiedChargeSheetLineItemInput,
  RetailOrderLineItem,
  UpdateRetailOrderLineItemInput,
} from '~/api/graphql/generated/types'
import { KibUserSelect } from '~/components/common/inputs/user-select/KibUserSelect'
import {
  calculatePrepaidColumns,
  getIsRetailOrderLineItem,
  getItemCoveredByWplan,
} from '~/components/dashboard/invoices/invoiceUtils'
import ChargesActions from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/ChargesActions'
import DragHandleIcon from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/DragHandleIcon'
import NumberField from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/NumberField'
import MembershipIcon from '~/components/dashboard/soapV2/order/MembershipIcon'
import FeatureToggle from '~/constants/featureToggle'
import { InventoryCategoryName } from '~/constants/inventoryCategory'
import { editRetailOrderLineItem } from '~/store/actions/finance'
import {
  editChargeSheetItem,
  editChargeSheetProducerItemBatch,
  editChargeSheetRetailOrderLineItemProducerBatch,
} from '~/store/duck/clientFinanceData'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { useHasGroupedParentWithFixedPrice } from '~/store/hooks/charges'
import {
  getFeatureToggle,
  getInventoryCategory,
} from '~/store/reducers/constants'
import { getInvoiceGroupIdIsExpanded } from '~/store/reducers/finance'
import type { InvoiceLineItem } from '~/types'
import {
  convertInvoiceLineItemToSoapLog,
  convertRetailOrderLineItemToSoapLog,
  getInventoryItemCategoryId,
} from '~/utils/orderUtils'
import { getPrescriptionType } from '~/utils/prescription'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChirpSoapChargesTable.module.scss'

export interface ChirpSoapChargesTableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  bundleItems?: InvoiceLineItem[]
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  draggableProps?: DraggableProvidedDraggableProps | null
  editDisabled?: boolean
  isBundle?: boolean
  isDragging?: boolean
  isNested?: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  onBundleDeclineClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onBundleDeleteClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onCalculatorClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onDeclineClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onDeleteClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onPrescriptionClick?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  onToggleExpand?: (item: InvoiceLineItem | RetailOrderLineItem) => void
  parentItem?: InvoiceLineItem | RetailOrderLineItem
  soapId?: string | Nil
}

const ChirpSoapChargesTableRow = forwardRef<
  HTMLTableRowElement,
  ChirpSoapChargesTableRowProps
>(
  (
    {
      bundleItems,
      dragHandleProps,
      draggableProps,
      editDisabled = false,
      isBundle = false,
      isDragging = false,
      isNested = false,
      item,
      onBundleDeclineClick,
      onBundleDeleteClick,
      onCalculatorClick,
      onDeclineClick,
      onDeleteClick,
      onPrescriptionClick,
      onToggleExpand,
      parentItem,
      soapId,
      className,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const isRetailOrderLineItem = getIsRetailOrderLineItem(item)
    const hasParentWithFixedPrice =
      useHasGroupedParentWithFixedPrice(parentItem)
    const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()

    // Get selectors needed for prescribable check
    const InventoryCategory = useSelector(getInventoryCategory)
    const isFoodCatalogEnabled = useSelector(
      getFeatureToggle(FeatureToggle.FOOD_CATALOG),
    )

    // Determine if item is prescribable
    const isPrescribable = React.useMemo(() => {
      if (isBundle) return false

      const convertedItem = isRetailOrderLineItem
        ? convertRetailOrderLineItemToSoapLog(item)
        : convertInvoiceLineItemToSoapLog(item)

      const drugCategoryId = Utils.findConstantIdByName(
        InventoryCategoryName.DRUG,
        InventoryCategory,
      )
      const vetDietCategoryId = Utils.findConstantIdByName(
        InventoryCategoryName.VET_DIET,
        InventoryCategory,
      )
      const itemCategoryId = getInventoryItemCategoryId(
        convertedItem,
        isFoodCatalogEnabled,
      )
      const isDrugOrVetDiet =
        itemCategoryId === drugCategoryId ||
        itemCategoryId === vetDietCategoryId

      const foodCategoryId = Utils.findConstantIdByName(
        InventoryCategoryName.FOOD,
        InventoryCategory,
      )
      const isFood = itemCategoryId === foodCategoryId
      const requiresPrescription = convertedItem.inventory?.requiresPrescription

      return isDrugOrVetDiet || (isFood && requiresPrescription)
    }, [
      isBundle,
      isRetailOrderLineItem,
      item,
      InventoryCategory,
      isFoodCatalogEnabled,
    ])

    // Get expand state for bundles - use group property if available, fallback to id
    const groupId = isRetailOrderLineItem
      ? null
      : (item as InvoiceLineItem).group
    const expandKey = groupId || item.id
    const isExpanded = useSelector(
      getInvoiceGroupIdIsExpanded(expandKey, soapId),
    )

    // Get item name - bundles use groupName, regular items use name/nameTranslation
    const itemName = isBundle
      ? (item as InvoiceLineItem).groupName ||
        LanguageUtils.getTranslatedFieldName(item) ||
        ''
      : LanguageUtils.getTranslatedFieldName(item) || ''

    // Determine if we should display Chewy icon
    const { isChewyActiveRx, isChewyReactiveRx } = isRetailOrderLineItem
      ? { isChewyActiveRx: true, isChewyReactiveRx: false }
      : getPrescriptionType(
          (item as InvoiceLineItem).prescriptionType,
          (item as InvoiceLineItem).origin,
        )

    const displayChewyIcon = isRetailOrderLineItem
      ? isChewyCheckoutEnabled
      : (isChewyActiveRx || isChewyReactiveRx) && isChewyCheckoutEnabled

    // Determine if we should display wellness icon
    const isWplanItem = isRetailOrderLineItem
      ? false
      : Boolean((item as InvoiceLineItem).wplanLogId)
    const coveredByWplan = getItemCoveredByWplan(item)

    // Check if item has discount and calculate original price
    const hasDiscount =
      !isRetailOrderLineItem &&
      (((item as InvoiceLineItem).discountAmount != null &&
        (item as InvoiceLineItem).discountAmount! > 0) ||
        ((item as InvoiceLineItem).discountPerc != null &&
          (item as InvoiceLineItem).discountPerc! > 0))

    const calculateOriginalPrice = () => {
      // For wellness items that are covered, use the extendedPrice as the original price
      if (isWplanItem && coveredByWplan && !isRetailOrderLineItem) {
        const invoiceItem = item as InvoiceLineItem
        return invoiceItem.extendedPrice || 0
      }

      if (!hasDiscount || isRetailOrderLineItem) return item.subTotal

      const invoiceItem = item as InvoiceLineItem
      const currentSubTotal = item.subTotal || 0

      if (
        invoiceItem.discountAmount != null &&
        invoiceItem.discountAmount > 0
      ) {
        return currentSubTotal + invoiceItem.discountAmount
      }
      if (invoiceItem.discountPerc != null && invoiceItem.discountPerc > 0) {
        // discountPerc is stored as decimal (e.g., 0.15 for 15%)
        // originalPrice * (1 - discountPerc) = currentSubTotal
        // originalPrice = currentSubTotal / (1 - discountPerc)
        // Guard against 100% discount (discountPerc = 1.0) to prevent division by zero
        if (invoiceItem.discountPerc >= 1) {
          return currentSubTotal
        }
        return currentSubTotal / (1 - invoiceItem.discountPerc)
      }

      return currentSubTotal
    }

    const originalPrice = calculateOriginalPrice()

    const shouldShowOriginalPrice =
      hasDiscount || (isWplanItem && coveredByWplan)

    // Debounced handlers for quantity changes
    const handleEditInvoiceLineItem = useDebouncedCallback(
      (payload: { quantity: number }) => {
        const data: ModifiedChargeSheetLineItemInput = {
          ...payload,
          prepaid: (item as InvoiceLineItem).prepaid,
          id: item.id,
          expectedModification: item.modificationDate,
        }
        dispatch(editChargeSheetItem({ updateItemInput: [data] }))
      },
      Defaults.DEBOUNCE_ACTION_TIME,
    )

    const handleEditRetailOrderLineItem = useDebouncedCallback(
      (payload: { quantity: number }) => {
        const retailItem = item as RetailOrderLineItem
        const data: UpdateRetailOrderLineItemInput = {
          ...payload,
          id: retailItem.id,
          quantity: payload.quantity,
          declined: retailItem.declined,
          partNumber: retailItem.partNumber,
          expectedModificationDate: retailItem.modificationDate,
          logExpectedModificationDate: retailItem.prescription?.updatedAt,
          autoshipFrequency: retailItem.autoshipFrequency,
          autoshipUnitId: retailItem.autoshipUnit?.id,
        }
        dispatch(editRetailOrderLineItem(data))
      },
      Defaults.DEBOUNCE_ACTION_TIME,
    )

    const handleQuantityChange = (value: string) => {
      const numericValue = parseFloat(value)

      // Only trigger API call for valid numbers >= minimum
      if (Number.isNaN(numericValue) || numericValue < 0.01) {
        return
      }

      if (isRetailOrderLineItem) {
        handleEditRetailOrderLineItem({ quantity: numericValue })
      } else if ((item as InvoiceLineItem).prepaid) {
        // For prepaid items, calculatePrepaidColumns may adjust quantity
        // when prepaidRemaining would go negative
        const newItem = { ...item, quantity: numericValue } as InvoiceLineItem
        calculatePrepaidColumns(item as InvoiceLineItem, newItem, false)
        handleEditInvoiceLineItem({ quantity: newItem.quantity })
      } else {
        handleEditInvoiceLineItem({ quantity: numericValue })
      }
    }

    // Producer change handler
    const handleProducerChange = (producerId: string) => {
      if (isRetailOrderLineItem) {
        dispatch(
          editChargeSheetRetailOrderLineItemProducerBatch([
            {
              id: item.id,
              producerId: producerId || '',
              expectedModification: item.modificationDate,
            },
          ]),
        )
      } else {
        dispatch(
          editChargeSheetProducerItemBatch({
            clientId: (item as InvoiceLineItem).clientId,
            updateItemProducerInput: [
              {
                producerId,
                id: item.id,
                expectedModification: item.modificationDate,
              },
            ],
          }),
        )
      }
    }

    const handleExpandClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onToggleExpand) {
        onToggleExpand(item)
      }
    }

    const handlePrescriptionClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onPrescriptionClick) {
        onPrescriptionClick(item)
      }
    }

    // NOTE: Must return single <tr> element for react-beautiful-dnd to work correctly.
    // For bundles with children, we render them inside a nested table within a single <td colspan>
    if (isBundle && bundleItems && bundleItems.length > 0) {
      return (
        <tr
          className={classNames(
            styles.dataRow,
            {
              [styles.dataRowDragging]: isDragging,
              [styles.declined]: item.declined,
            },
            className,
          )}
          ref={ref}
          {...draggableProps}
          {...dragHandleProps}
        >
          <td className={styles.bundleCell} colSpan={5}>
            {/* Parent bundle row content - using table for proper column alignment */}
            <table className={styles.bundleParentTable}>
              <colgroup>
                <col style={{ width: '307px' }} />
                <col style={{ width: '146px' }} />
                <col style={{ width: '55px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '110px' }} />
              </colgroup>
              <tbody>
                <tr className={styles.bundleParentRow}>
                  {/* Item column with drag handle and expand button */}
                  <td
                    className={classNames(styles.dataCell, styles.dataCellItem)}
                  >
                    <span className={styles.dragHandle}>
                      <DragHandleIcon size={24} />
                    </span>
                    <KibButtonNew
                      aria-label={
                        isExpanded
                          ? t('Common:COLLAPSE_BUNDLE')
                          : t('Common:EXPAND_BUNDLE')
                      }
                      className={styles.expandButton}
                      emphasis="tertiary"
                      icon={
                        isExpanded ? (
                          <KibChevronUpIcon />
                        ) : (
                          <KibChevronDownIcon />
                        )
                      }
                      onClick={handleExpandClick}
                    />
                    <div className={styles.itemNameWithIcons}>
                      {displayChewyIcon && (
                        <KibChewyIcon className={styles.chewyIcon} />
                      )}
                      {onPrescriptionClick && !isBundle ? (
                        <KibButtonNew
                          className={styles.prescriptionLink}
                          emphasis="tertiary"
                          onClick={handlePrescriptionClick}
                        >
                          {itemName}
                        </KibButtonNew>
                      ) : (
                        <span className={styles.itemName} title={itemName}>
                          {itemName}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Empty Producer column */}
                  <td
                    aria-label={t('Common:PRODUCER_NOT_APPLICABLE_FOR_BUNDLES')}
                    className={styles.dataCell}
                  />

                  {/* Empty Quantity column */}
                  <td
                    aria-label={t('Common:QUANTITY_NOT_APPLICABLE_FOR_BUNDLES')}
                    className={styles.dataCell}
                  />

                  {/* Actions column */}
                  <td
                    className={classNames(
                      styles.dataCell,
                      styles.dataCellActions,
                    )}
                  >
                    <ChargesActions
                      showDeclineButton
                      showDeleteButton
                      declineHighlighted={item.declined}
                      disabledCalculator={editDisabled}
                      disabledDecline={editDisabled}
                      disabledDelete={editDisabled}
                      showCalculatorButton={false}
                      onDeclineClick={() => onBundleDeclineClick?.(item)}
                      onDeleteClick={() => onBundleDeleteClick?.(item)}
                    />
                  </td>

                  {/* Total column */}
                  <td
                    className={classNames(
                      styles.dataCell,
                      styles.dataCellTotal,
                    )}
                  >
                    <div className={styles.priceContainer}>
                      {isWplanItem && !item.declined && (
                        <MembershipIcon
                          className={styles.membershipIcon}
                          limitReached={!coveredByWplan}
                        />
                      )}
                      <span className={styles.price}>
                        {NumberUtils.formatMoney(item.subTotal)}
                      </span>
                      {shouldShowOriginalPrice && (
                        <span className={styles.originalPrice}>
                          {NumberUtils.formatMoney(originalPrice)}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Nested table for bundle children */}
            {isExpanded && (
              <table className={styles.nestedTable}>
                <tbody>
                  {bundleItems.map((childItem) => (
                    <ChirpSoapChargesTableRow
                      isNested
                      editDisabled={editDisabled}
                      item={childItem}
                      key={`${childItem.bundleId}_${childItem.id}`}
                      parentItem={item as InvoiceLineItem}
                      soapId={soapId}
                      onCalculatorClick={onCalculatorClick}
                      onDeclineClick={onDeclineClick}
                      onDeleteClick={onDeleteClick}
                      onPrescriptionClick={onPrescriptionClick}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )
    }

    // Regular (non-bundle) or nested row
    return (
      <tr
        className={classNames(
          styles.dataRow,
          {
            [styles.dataRowDragging]: isDragging,
            [styles.dataRowNested]: isNested,
            [styles.declined]: item.declined,
          },
          className,
        )}
        ref={ref}
        {...draggableProps}
        {...dragHandleProps}
      >
        {/* Item cell with drag handle and expand icon */}
        <td className={styles.dataCell}>
          <div
            className={classNames(styles.dataCellItem, {
              [styles.dataCellItemNested]: isNested,
            })}
          >
            {!isNested && (
              <span className={styles.dragHandle}>
                <DragHandleIcon size={24} />
              </span>
            )}
            {isBundle && (
              <KibButtonNew
                aria-label={
                  isExpanded
                    ? t('Common:COLLAPSE_BUNDLE')
                    : t('Common:EXPAND_BUNDLE')
                }
                className={styles.expandButton}
                emphasis="tertiary"
                icon={
                  isExpanded ? <KibChevronUpIcon /> : <KibChevronDownIcon />
                }
                onClick={handleExpandClick}
              />
            )}
            {isPrescribable && onPrescriptionClick ? (
              <>
                {displayChewyIcon && (
                  <KibChewyIcon className={styles.chewyIcon} />
                )}
                <KibButtonNew
                  className={styles.prescriptionLink}
                  emphasis="tertiary"
                  title={itemName}
                  onClick={handlePrescriptionClick}
                >
                  {itemName}
                </KibButtonNew>
              </>
            ) : (
              <>
                {displayChewyIcon && (
                  <KibChewyIcon className={styles.chewyIcon} />
                )}
                <span className={styles.itemName} title={itemName}>
                  {itemName}
                </span>
              </>
            )}
          </div>
        </td>

        {/* Producer cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellProducer)}
          onClick={(e) => e.stopPropagation()}
        >
          {!item.declined && !isBundle && (
            <KibUserSelect
              reset
              disabled={editDisabled}
              placeHolder=""
              userSelectStateId={`producer-${item.id}`}
              value={item.producerId || ''}
              onChange={handleProducerChange}
            />
          )}
        </td>

        {/* Quantity cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellQuantity)}
          onClick={(e) => e.stopPropagation()}
        >
          {!item.declined && !isBundle && (
            <NumberField
              disabled={editDisabled}
              label=""
              min={0.01}
              step="0.01"
              value={String(item.quantity || 0)}
              onChange={handleQuantityChange}
            />
          )}
        </td>

        {/* Actions cell */}
        <td
          className={classNames(styles.dataCell, styles.dataCellActions)}
          onClick={(e) => e.stopPropagation()}
        >
          {!isBundle && (
            <ChargesActions
              declineHighlighted={item.declined}
              disabledCalculator={editDisabled}
              disabledDecline={editDisabled}
              disabledDelete={editDisabled}
              showCalculatorButton={!hasParentWithFixedPrice}
              onCalculatorClick={() => onCalculatorClick?.(item)}
              onDeclineClick={() => onDeclineClick?.(item)}
              onDeleteClick={() => onDeleteClick?.(item)}
            />
          )}
        </td>

        {/* Total cell */}
        <td className={classNames(styles.dataCell, styles.dataCellTotal)}>
          {!hasParentWithFixedPrice && (
            <div className={styles.priceContainer}>
              {isWplanItem && !item.declined && (
                <MembershipIcon
                  className={styles.membershipIcon}
                  limitReached={!coveredByWplan}
                />
              )}
              <span className={styles.price}>
                {NumberUtils.formatMoney(item.subTotal)}
              </span>
              {shouldShowOriginalPrice && (
                <span className={styles.originalPrice}>
                  {NumberUtils.formatMoney(originalPrice)}
                </span>
              )}
            </div>
          )}
        </td>
      </tr>
    )
  },
)

export default ChirpSoapChargesTableRow
